// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Payment Form page (Stage 6P.7).
//
// Routes:
//   /erp/supplier-payments/new            (create draft)
//   /erp/supplier-payments/:id            (edit / post / cancel)
//
// Drives the Stage 6P.7 supplier-payment + payment-voucher pipeline:
//   * Save Draft        → POST  /api/erp/supplier-payments         (new)
//                       → PUT   /api/erp/supplier-payments/:id     (existing draft)
//   * Preview Posting   → GET   /api/erp/supplier-payments/:id/preview
//   * Post Payment      → POST  /api/erp/supplier-payments/:id/post
//   * Cancel Payment    → POST  /api/erp/supplier-payments/:id/cancel
//
// Backend remains the source of truth for ALL math:
//   payment_no allocation, ledger code resolution (payment_account
//   + supplier_payable + SUPPLIER-ADVANCE chain), payable balance
//   computation, BILL_WISE / ADVANCE / ON_ACCOUNT invariants, voucher-
//   Posting integration, duplicate-block decisions.
// Frontend mirrors for instant UX but never wins.
//
// Layout mirrors PostPurchaseBillAccountingPage but extends for the
// more complex allocation workflow:
//   * Header card             (Payment No, Date, Supplier, GSTIN,
//                              Type, Method, Payment Account, Reference,
//                              Bank, Cheque, UTR, Amount, Round-Off,
//                              Remarks, status badges)
//   * Ledger Mapping panel    (supplier_payable + payment_account roles
//                              resolved server-side; missing → red)
//   * Allocation Grid         (BILL_WISE only — Bill No, Invoice, Date,
//                              Payable, Already Paid, Balance Before,
//                              Allocation Amount, Balance After, Remove)
//   * Add-Bill Drawer         (UNPAID / PARTIALLY_PAID bills for the
//                              selected supplier, multi-select with
//                              auto-populated balances)
//   * Summary card            (Payment Amount, Allocated, Unallocated,
//                              Difference, Voucher No, Posting Status)
//   * Footer toolbar          (Save Draft · Preview Posting · Post
//                              Payment · Cancel Payment · Back)
//   * Result panel            (after post — accounting voucher no +
//                              posting_no + posted_at; success/failure)
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Result,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import {
  ArrowLeftOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  SendOutlined,
  StopOutlined,
  WarningOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { supplierPaymentApi } from './supplierPaymentApi';
import { buildDropdownStateProps } from '../common/dropdownLoaderState.js';
import {
  PAYMENT_TYPES,
  PAYMENT_METHODS,
  buildSupplierPaymentPayload,
  calculatePaymentAllocationTotals,
  canCancelSupplierPayment,
  canEditSupplierPayment,
  canPostSupplierPayment,
  normalizeSupplierPayableRow,
  paymentMethodLabel,
  paymentStatusColor,
  paymentStatusLabel,
  postingStatusColor,
  postingStatusLabel,
  validatePaymentMatchesAllocated,
  validateSupplierPayment,
  validateSupplierPaymentAllocation
} from './supplierPaymentHelpers';

import client from '../../../api/client';

const { Title, Text, Paragraph } = Typography;


// ─────────────────────────────────────────────────────────────────
// Tolerant numeric formatter — preview numbers arrive as JS numbers
// from the API. Zero collapses to a dim '—' so empty cells stay
// scannable in the per-row debit / credit columns.
// ─────────────────────────────────────────────────────────────────
function fmtMoney(v, decimals = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '—';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

function fmtMoneyAlways(v, decimals = 2) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

function fmtDate(v) {
  if (!v) return '—';
  const s = String(v);
  if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
}

function fmtDateTime(v) {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isFinite(d.getTime())) {
      return d.toLocaleString(undefined, {
        year:   'numeric',
        month:  '2-digit',
        day:    '2-digit',
        hour:   '2-digit',
        minute: '2-digit'
      });
    }
  } catch (_) { /* fall through */ }
  return String(v);
}

function toDayjsOrNull(v) {
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
}

function isoOrNull(v) {
  if (!v) return null;
  if (typeof v === 'string') return v;
  if (v && typeof v.format === 'function') return v.format('YYYY-MM-DD');
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return v.toISOString().slice(0, 10);
  }
  return null;
}


// ─────────────────────────────────────────────────────────────────
// Method-aware visibility — bank/cheque/UTR fields only matter for
// instrument-based methods. Cash hides everything; UPI shows UTR.
// ─────────────────────────────────────────────────────────────────
const METHODS_WITH_BANK   = new Set(['BANK', 'CHEQUE', 'NEFT', 'RTGS', 'IMPS']);
const METHODS_WITH_CHEQUE = new Set(['CHEQUE']);
const METHODS_WITH_UTR    = new Set(['BANK', 'UPI', 'NEFT', 'RTGS', 'IMPS']);

function showBankFor(method) {
  return METHODS_WITH_BANK.has(String(method || '').toUpperCase());
}
function showChequeFor(method) {
  return METHODS_WITH_CHEQUE.has(String(method || '').toUpperCase());
}
function showUtrFor(method) {
  return METHODS_WITH_UTR.has(String(method || '').toUpperCase());
}


// ─────────────────────────────────────────────────────────────────
// Ledger-mapping row builder — preview returns a `resolvedLedgers`
// shape of:
//   { supplierPayable, paymentAccount }
// Either may be `null` when the resolver couldn't bind a ledger
// (most common: ADVANCE / ON_ACCOUNT with SUPPLIER-ADVANCE not seeded).
// We render a flat row list with red "Missing" tags so the operator
// can see the gap at a glance.
// ─────────────────────────────────────────────────────────────────
function buildLedgerMappingRows(resolvedLedgers) {
  const rl = resolvedLedgers && typeof resolvedLedgers === 'object'
    ? resolvedLedgers
    : {};
  const rows = [];

  const slots = [
    { key: 'supplierPayable',
      role: 'Supplier Payable (Dr)',
      ledger: rl.supplierPayable || rl.supplier_payable || rl.supplier || null },
    { key: 'paymentAccount',
      role: 'Payment Account (Cr)',
      ledger: rl.paymentAccount || rl.payment_account || rl.bankCash || null }
  ];

  for (const s of slots) {
    const l = s.ledger;
    rows.push({
      key:         `singular:${s.key}`,
      role:        s.role,
      accountCode: l && (l.account_code || l.accountCode) ? (l.account_code || l.accountCode) : null,
      accountName: l && (l.account_name || l.accountName) ? (l.account_name || l.accountName) : null,
      missing:     !l || !(l.id || l.account_id || l.accountId)
    });
  }
  return rows;
}


// ─────────────────────────────────────────────────────────────────
// Main page component
// ─────────────────────────────────────────────────────────────────
export default function SupplierPaymentFormPage() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  const [form] = Form.useForm();

  // ── State ─────────────────────────────────────────────────────
  const isCreate = !routeId || routeId === 'new';

  const [payment,    setPayment]    = useState(null);     // loaded record
  const [preview,    setPreview]    = useState(null);     // server preview
  const [loading,    setLoading]    = useState(!isCreate);
  const [loadError,  setLoadError]  = useState(null);

  const [saving,     setSaving]     = useState(false);
  const [posting,    setPosting]    = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const [postResult, setPostResult] = useState(null);
  const [postError,  setPostError]  = useState(null);

  // Allocation grid lives outside the AntD Form so the editable
  // amount field can recompute the balance-after column without
  // triggering a full form re-render.
  const [allocations, setAllocations] = useState([]);

  // Add-Bill drawer state.
  const [drawerOpen,        setDrawerOpen]        = useState(false);
  const [drawerLoading,     setDrawerLoading]     = useState(false);
  const [drawerRows,        setDrawerRows]        = useState([]);
  const [drawerSelectedIds, setDrawerSelectedIds] = useState([]);

  // Lookups. `*Error` flags drive the AntD Select notFoundContent via
  // the shared `buildDropdownStateProps` helper so a failed loader
  // shows "Failed to load — try refresh" instead of a silent empty
  // dropdown (Stage 6P-MED-FIX F-FD-7).
  const [supplierOptions,  setSupplierOptions]  = useState([]);
  const [supplierLoading,  setSupplierLoading]  = useState(false);
  const [supplierError,    setSupplierError]    = useState(null);
  const [ledgerOptions,    setLedgerOptions]    = useState([]);
  const [ledgerLoading,    setLedgerLoading]    = useState(false);
  const [ledgerError,      setLedgerError]      = useState(null);

  // Watched form fields drive the conditional UI.
  const paymentType   = Form.useWatch('paymentType',   form);
  const paymentMethod = Form.useWatch('paymentMethod', form);
  const supplierId    = Form.useWatch('supplierId',    form);
  const paymentAmount = Form.useWatch('paymentAmount', form);
  const supplierGstin = Form.useWatch('supplierGstin', form);


  // ── Initial load ──────────────────────────────────────────────
  const loadPayment = useCallback(async () => {
    if (isCreate) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await supplierPaymentApi.getPayment(routeId);
      setPayment(data || null);
      if (data) {
        form.setFieldsValue({
          paymentDate:        toDayjsOrNull(data.paymentDate || data.payment_date),
          supplierId:         data.supplierId || data.supplier_id || null,
          supplierGstin:      data.supplierGstin || data.supplier_gstin || '',
          paymentType:        (data.paymentType || data.payment_type || 'BILL_WISE'),
          paymentMethod:      (data.paymentMethod || data.payment_method || 'BANK'),
          paymentAccountId:   data.paymentAccountId || data.payment_account_id || null,
          paymentAccountName: data.paymentAccountName || data.payment_account_name || '',
          referenceNo:        data.referenceNo || data.reference_no || data.bankReference || '',
          referenceDate:      toDayjsOrNull(data.referenceDate || data.reference_date),
          bankName:           data.bankName || data.bank_name || data.counterpartyBankName || '',
          chequeNo:           data.chequeNo || data.cheque_no || data.instrumentNo || '',
          utrNo:              data.utrNo || data.utr_no || data.bankReference || '',
          paymentAmount:      Number(data.paymentAmount || data.payment_amount || 0) || null,
          roundOff:           Number(data.roundOff || data.round_off || 0) || 0,
          remarks:            data.remarks || ''
        });
        const allocs = Array.isArray(data.allocations) ? data.allocations : [];
        setAllocations(
          allocs.map((a, idx) => ({
            key:                     a.id || `existing-${idx}`,
            id:                      a.id || null,
            purchaseBillId:          a.purchaseBillId || a.purchase_bill_id || null,
            purchaseAccountingPostingId:
                                     a.purchaseAccountingPostingId || a.purchase_accounting_posting_id || null,
            purchaseBillNo:          a.purchaseBillNo || a.purchase_bill_no || null,
            purchaseBillDate:        a.purchaseBillDate || a.purchase_bill_date || null,
            supplierInvoiceNo:       a.supplierInvoiceNo || a.supplier_invoice_no || null,
            supplierInvoiceDate:     a.supplierInvoiceDate || a.supplier_invoice_date || null,
            billGrandTotal:          Number(a.billGrandTotal || a.bill_grand_total || 0) || 0,
            billOutstandingBefore:   Number(a.billOutstandingBefore || a.bill_outstanding_before || 0) || 0,
            allocationAmount:        Number(a.allocationAmount || a.allocation_amount || a.allocatedAmount || a.allocated_amount || 0) || 0,
            billOutstandingAfter:    Number(a.billOutstandingAfter || a.bill_outstanding_after || 0) || 0,
            paidAmount:              Number(a.paidAmount || a.paid_amount || 0) || 0,
            payableAmount:           Number(a.payableAmount || a.payable_amount ||
                                            a.billGrandTotal || a.bill_grand_total || 0) || 0,
            remarks:                 a.remarks || ''
          }))
        );
      }
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to load supplier payment.';
      setLoadError(msg);
      setPayment(null);
    } finally {
      setLoading(false);
    }
  }, [isCreate, routeId, form]);

  useEffect(() => { loadPayment(); }, [loadPayment]);


  // ── Defaults for create-mode ──────────────────────────────────
  useEffect(() => {
    if (!isCreate) return;
    form.setFieldsValue({
      paymentDate:   dayjs(),
      paymentType:   'BILL_WISE',
      paymentMethod: 'BANK',
      roundOff:      0
    });
    // Intentional one-shot: setFieldsValue is stable across renders here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate]);


  // ── Supplier dropdown loader ──────────────────────────────────
  const loadSuppliers = useCallback(async (q = '') => {
    setSupplierLoading(true);
    setSupplierError(null);
    try {
      // Reuse the accounts/parties/suppliers lookup endpoint — it's
      // tenant-aware and active-only, the same source the Payment
      // Voucher modal uses.
      const resp = await client.get('/api/erp/accounts/parties/suppliers', {
        params: { ...(q ? { q } : {}), limit: 100 }
      });
      const rows = resp && resp.data && resp.data.data !== undefined
        ? resp.data.data
        : (resp && resp.data) || [];
      setSupplierOptions(Array.isArray(rows) ? rows : []);
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to load suppliers.';
      setSupplierError(msg);
      setSupplierOptions([]);
    } finally {
      setSupplierLoading(false);
    }
  }, []);

  useEffect(() => { loadSuppliers(''); }, [loadSuppliers]);

  // Auto-fill GSTIN when the supplier changes.
  useEffect(() => {
    if (!supplierId) return;
    const match = supplierOptions.find((s) =>
      (s.id || s.supplierId) === supplierId
    );
    if (match) {
      const g = match.gstin || match.supplierGstin || match.gstNumber || '';
      if (g) form.setFieldsValue({ supplierGstin: g });
    }
  }, [supplierId, supplierOptions, form]);


  // ── Payment-account dropdown loader ───────────────────────────
  // Pulls cash + bank ledgers via the same lookup the Payment
  // Voucher modal uses. Resolver chain on the backend remains the
  // authority — this dropdown is a convenience so operators don't
  // have to type the account_code.
  const loadLedgers = useCallback(async (q = '') => {
    setLedgerLoading(true);
    setLedgerError(null);
    try {
      const resp = await client.get('/api/erp/accounts/ledgers/cash-bank', {
        params: { ...(q ? { q } : {}), limit: 100 }
      });
      const rows = resp && resp.data && resp.data.data !== undefined
        ? resp.data.data
        : (resp && resp.data) || [];
      setLedgerOptions(Array.isArray(rows) ? rows : []);
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to load payment accounts.';
      setLedgerError(msg);
      setLedgerOptions([]);
    } finally {
      setLedgerLoading(false);
    }
  }, []);

  useEffect(() => { loadLedgers(''); }, [loadLedgers]);


  // ── Allocation math ───────────────────────────────────────────
  // Recompute balance-after for each allocation row on every change.
  const recomputeAllocations = useCallback((rows) => {
    return rows.map((r) => {
      const before = Number(r.billOutstandingBefore) || 0;
      const amt    = Number(r.allocationAmount) || 0;
      return {
        ...r,
        billOutstandingAfter: Math.round((before - amt) * 100) / 100
      };
    });
  }, []);

  const updateAllocation = useCallback((idx, patch) => {
    setAllocations((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], ...patch };
      return recomputeAllocations(next);
    });
  }, [recomputeAllocations]);

  const removeAllocation = useCallback((idx) => {
    setAllocations((prev) => {
      const next = prev.slice();
      next.splice(idx, 1);
      return next;
    });
  }, []);

  // Totals — kept in sync with the editable grid.
  const allocationTotals = useMemo(() => {
    const { allocatedAmount } = calculatePaymentAllocationTotals(
      allocations.map((a) => ({
        allocation_amount: a.allocationAmount,
        status: 'ACTIVE'
      }))
    );
    const amount = Number(paymentAmount) || 0;
    const unallocated = Math.round((amount - allocatedAmount) * 100) / 100;
    const difference = String(paymentType || '').toUpperCase() === 'BILL_WISE'
      ? unallocated
      : 0;
    return { allocated: allocatedAmount, unallocated, difference };
  }, [allocations, paymentAmount, paymentType]);


  // ── Add-Bill drawer ───────────────────────────────────────────
  const openAddBillDrawer = useCallback(async () => {
    if (!supplierId) {
      message.warning('Select a supplier first.');
      return;
    }
    setDrawerSelectedIds([]);
    setDrawerOpen(true);
    setDrawerLoading(true);
    try {
      const rows = await supplierPaymentApi.listPayables({
        supplier_id:    supplierId,
        balance_status: 'UNPAID,PARTIALLY_PAID',
        limit:          200
      });
      const list = Array.isArray(rows) ? rows : (rows && rows.rows) || [];
      // Exclude bills already in the allocation grid.
      const existingIds = new Set(
        allocations
          .map((a) => a.purchaseBillId)
          .filter(Boolean)
          .map(String)
      );
      const normalised = list
        .map(normalizeSupplierPayableRow)
        .filter(Boolean)
        .filter((r) => !existingIds.has(String(r.purchaseBillId || '')));
      setDrawerRows(normalised);
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to load unpaid bills.';
      message.error(msg);
      setDrawerRows([]);
    } finally {
      setDrawerLoading(false);
    }
  }, [supplierId, allocations]);

  const handleAddBillsConfirm = useCallback(() => {
    if (drawerSelectedIds.length === 0) {
      setDrawerOpen(false);
      return;
    }
    const additions = drawerRows
      .filter((r) => drawerSelectedIds.includes(r.purchaseBillId))
      .map((r) => ({
        key:                         `add-${r.purchaseBillId}-${Date.now()}`,
        id:                          null,
        purchaseBillId:              r.purchaseBillId,
        purchaseAccountingPostingId: r.postingId || null,
        purchaseBillNo:              r.purchaseBillNo,
        purchaseBillDate:            r.purchaseBillDate,
        supplierInvoiceNo:           r.supplierInvoiceNo || null,
        supplierInvoiceDate:         r.supplierInvoiceDate || null,
        billGrandTotal:              Number(r.payableAmount) || 0,
        payableAmount:               Number(r.payableAmount) || 0,
        paidAmount:                  Number(r.paidAmount) || 0,
        billOutstandingBefore:       Number(r.outstandingAmount) || 0,
        // Default the allocation to clear the outstanding balance.
        allocationAmount:            Number(r.outstandingAmount) || 0,
        billOutstandingAfter:        0,
        remarks:                     ''
      }));
    setAllocations((prev) => recomputeAllocations(prev.concat(additions)));
    setDrawerOpen(false);
    setDrawerSelectedIds([]);
  }, [drawerRows, drawerSelectedIds, recomputeAllocations]);


  // ── Build payload + validate ──────────────────────────────────
  const buildPayload = useCallback(async () => {
    const values = await form.validateFields();
    const allocationLines = allocations
      .filter((a) => a.purchaseBillId || a.purchaseAccountingPostingId)
      .map((a) => ({
        purchase_bill_id:               a.purchaseBillId || null,
        purchase_accounting_posting_id: a.purchaseAccountingPostingId || null,
        allocation_amount:              Number(a.allocationAmount) || 0,
        remarks:                        a.remarks || ''
      }));
    const formValues = {
      paymentDate:        isoOrNull(values.paymentDate),
      supplierId:         values.supplierId,
      supplierName: (supplierOptions.find(
        (s) => (s.id || s.supplierId) === values.supplierId
      ) || {}).supplierName ||
        (supplierOptions.find(
          (s) => (s.id || s.supplierId) === values.supplierId
        ) || {}).name ||
        (payment && payment.supplierName) || '',
      supplierGstin:      values.supplierGstin || '',
      paymentType:        values.paymentType,
      paymentMethod:      values.paymentMethod,
      paymentAccountId:   values.paymentAccountId || null,
      paymentAccountName: (() => {
        const m = ledgerOptions.find((l) => (l.id || l.accountId) === values.paymentAccountId);
        return (m && (m.accountName || m.account_name)) || values.paymentAccountName || '';
      })(),
      referenceNo:        values.referenceNo || '',
      referenceDate:      isoOrNull(values.referenceDate),
      bankName:           values.bankName || '',
      chequeNo:           values.chequeNo || '',
      utrNo:              values.utrNo || '',
      paymentAmount:      values.paymentAmount,
      roundOff:           values.roundOff || 0,
      remarks:            values.remarks || '',
      allocations:        allocationLines
    };
    return buildSupplierPaymentPayload(formValues);
  }, [form, allocations, supplierOptions, ledgerOptions, payment]);


  // ── Frontend validation summary (UI mirror) ───────────────────
  const validationErrors = useMemo(() => {
    const errs = [];
    if (!paymentType) errs.push('Payment type is required.');
    if (!paymentMethod) errs.push('Payment method is required.');
    const amount = Number(paymentAmount) || 0;
    if (!(amount > 0)) errs.push('Payment amount must be greater than zero.');

    const type = String(paymentType || '').toUpperCase();
    if (type === 'BILL_WISE') {
      if (allocations.length === 0) {
        errs.push('BILL_WISE payments must have at least one allocation.');
      }
      // Sum vs amount.
      if (
        amount > 0 &&
        Math.abs(allocationTotals.allocated - amount) > 0.01
      ) {
        errs.push(
          `Allocated (${fmtMoneyAlways(allocationTotals.allocated)}) must equal Payment Amount (${fmtMoneyAlways(amount)}).`
        );
      }
      // Per-row outstanding check.
      for (const a of allocations) {
        const v = validateSupplierPaymentAllocation(
          {
            purchase_bill_id:   a.purchaseBillId,
            allocation_amount:  a.allocationAmount
          },
          a.billOutstandingBefore
        );
        if (!v.ok) {
          for (const e of v.errors) {
            errs.push(
              `${a.purchaseBillNo || 'Bill'} — ${e.message}`
            );
          }
        }
      }
    } else if (type === 'ADVANCE' || type === 'ON_ACCOUNT') {
      if (allocations.length > 0) {
        errs.push(`${type} payments cannot carry allocations.`);
      }
    }
    return errs;
  }, [paymentType, paymentMethod, paymentAmount, allocations, allocationTotals]);


  // ── Save / Update draft ───────────────────────────────────────
  const handleSaveDraft = useCallback(async () => {
    setSaving(true);
    setPostError(null);
    try {
      const payload = await buildPayload();
      const localCheck = validateSupplierPayment(payload);
      if (!localCheck.ok) {
        message.error(localCheck.errors[0].message || 'Validation failed.');
        setSaving(false);
        return;
      }
      let saved;
      if (isCreate || !payment || !payment.id) {
        saved = await supplierPaymentApi.createPayment(payload);
        message.success('Draft created.');
        if (saved && saved.id) {
          navigate(`/erp/supplier-payments/${saved.id}`, { replace: true });
        }
      } else {
        saved = await supplierPaymentApi.updatePayment(payment.id, payload);
        message.success('Draft updated.');
      }
      setPayment(saved || payment);
      // Refresh allocations from server response so server-side
      // outstanding-after recomputation flows back into the grid.
      if (saved && Array.isArray(saved.allocations)) {
        setAllocations(
          saved.allocations.map((a, idx) => ({
            key:                         a.id || `existing-${idx}`,
            id:                          a.id || null,
            purchaseBillId:              a.purchaseBillId || a.purchase_bill_id || null,
            purchaseAccountingPostingId: a.purchaseAccountingPostingId || a.purchase_accounting_posting_id || null,
            purchaseBillNo:              a.purchaseBillNo || a.purchase_bill_no || null,
            purchaseBillDate:            a.purchaseBillDate || a.purchase_bill_date || null,
            supplierInvoiceNo:           a.supplierInvoiceNo || a.supplier_invoice_no || null,
            supplierInvoiceDate:         a.supplierInvoiceDate || a.supplier_invoice_date || null,
            billGrandTotal:              Number(a.billGrandTotal || a.bill_grand_total || 0) || 0,
            payableAmount:               Number(a.payableAmount || a.payable_amount ||
                                                a.billGrandTotal || a.bill_grand_total || 0) || 0,
            paidAmount:                  Number(a.paidAmount || a.paid_amount || 0) || 0,
            billOutstandingBefore:       Number(a.billOutstandingBefore || a.bill_outstanding_before || 0) || 0,
            allocationAmount:            Number(a.allocationAmount || a.allocation_amount || a.allocatedAmount || a.allocated_amount || 0) || 0,
            billOutstandingAfter:        Number(a.billOutstandingAfter || a.bill_outstanding_after || 0) || 0,
            remarks:                     a.remarks || ''
          }))
        );
      }
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.errorFields ? 'Please fix the highlighted fields.' : null)
        || (e && e.message)
        || 'Failed to save draft.';
      message.error(msg);
      setPostError(msg);
    } finally {
      setSaving(false);
    }
  }, [buildPayload, isCreate, navigate, payment]);


  // ── Preview posting ───────────────────────────────────────────
  const handlePreviewPosting = useCallback(async () => {
    if (!payment || !payment.id) {
      message.warning('Save the draft before requesting a posting preview.');
      return;
    }
    setPreviewing(true);
    setPostError(null);
    try {
      const data = await supplierPaymentApi.previewPosting(payment.id);
      setPreview(data || null);
      if (data && Array.isArray(data.blockers) && data.blockers.length > 0) {
        message.warning(`Preview returned ${data.blockers.length} blocker${data.blockers.length === 1 ? '' : 's'}.`);
      } else {
        message.success('Preview ready.');
      }
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to load posting preview.';
      message.error(msg);
      setPostError(msg);
    } finally {
      setPreviewing(false);
    }
  }, [payment]);


  // ── Post payment ──────────────────────────────────────────────
  const handlePost = useCallback(async () => {
    if (!payment || !payment.id) {
      message.warning('Save the draft before posting.');
      return;
    }
    setPosting(true);
    setPostError(null);
    try {
      const result = await supplierPaymentApi.postPayment(payment.id, {});

      const postingRow = (result && result.posting)   || null;
      const voucherRow = (result && result.voucher)   || null;
      const errorBlob  = (result && result.error)     || null;

      const status =
        (postingRow && (postingRow.status || postingRow.postingStatus)) ||
        (result && (result.status || result.postingStatus)) ||
        (errorBlob ? 'POSTING_FAILED' : 'POSTED');

      const postingNo =
        (postingRow && (postingRow.postingNo || postingRow.posting_no)) ||
        (result && (result.postingNo || result.posting_no)) ||
        null;

      const voucherNo =
        (voucherRow && (voucherRow.voucherNo || voucherRow.voucher_no)) ||
        (postingRow && (postingRow.voucherNo || postingRow.voucher_no ||
                        postingRow.accountingVoucherNo || postingRow.accounting_voucher_no)) ||
        (result && (result.voucherNo || result.voucher_no)) ||
        null;

      const postedAt =
        (postingRow && (postingRow.postedAt || postingRow.posted_at)) ||
        (result && (result.postedAt || result.posted_at)) ||
        null;

      setPostResult({
        success:      !errorBlob && String(status).toUpperCase() === 'POSTED',
        status,
        postingNo,
        voucherNo,
        postedAt,
        errorMessage: errorBlob ? (errorBlob.message || errorBlob.code) : null
      });

      if (!errorBlob && String(status).toUpperCase() === 'POSTED') {
        message.success(`Posted → ${voucherNo || 'accounting voucher'}.`);
      } else {
        message.warning(
          errorBlob && errorBlob.message
            ? `Posting failed: ${errorBlob.message}`
            : 'Posting did not complete successfully.'
        );
      }
      // Re-fetch so the header / footer status badges update.
      await loadPayment();
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to post the payment.';
      setPostError(msg);
      message.error(msg);
    } finally {
      setPosting(false);
    }
  }, [payment, loadPayment]);


  // ── Cancel payment ────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    if (!payment || !payment.id) return;
    let reason = '';
    Modal.confirm({
      title: 'Cancel this supplier payment?',
      icon:  <ExclamationCircleOutlined />,
      content: (
        <div>
          <Paragraph>
            Cancelling reverses the accounting voucher and frees the
            allocated bill balances. This cannot be undone.
          </Paragraph>
          <Input.TextArea
            placeholder="Reason (optional)…"
            rows={3}
            onChange={(e) => { reason = e.target.value; }}
          />
        </div>
      ),
      okText:    'Cancel Payment',
      okType:    'danger',
      cancelText:'Back',
      async onOk() {
        setCancelling(true);
        try {
          await supplierPaymentApi.cancelPayment(payment.id, reason || undefined);
          message.success('Payment cancelled.');
          await loadPayment();
          setPreview(null);
          setPostResult(null);
        } catch (e) {
          const msg = (e && e.response && e.response.data &&
                        (e.response.data.message || e.response.data.code))
            || (e && e.message)
            || 'Failed to cancel payment.';
          message.error(msg);
        } finally {
          setCancelling(false);
        }
      }
    });
  }, [payment, loadPayment]);


  // ── Derived UI gates ──────────────────────────────────────────
  const status        = payment ? (payment.status || payment.payment_status || null) : null;
  const postingStatus = payment ? (payment.postingStatus || payment.posting_status || null) : null;
  const voucherNo     = payment ? (payment.voucherNo || payment.voucher_no || null) : null;

  const isEditable   = isCreate || canEditSupplierPayment(status);
  const canPost      = !!payment && canPostSupplierPayment(status) &&
                        validationErrors.length === 0 &&
                        validatePaymentMatchesAllocated({
                          payment_type:   paymentType,
                          payment_amount: paymentAmount,
                          allocations: allocations.map((a) => ({
                            allocation_amount: a.allocationAmount,
                            status: 'ACTIVE'
                          }))
                        });
  const canCancel    = !!payment && canCancelSupplierPayment(status);

  const ledgerRows = useMemo(
    () => buildLedgerMappingRows(preview && preview.resolvedLedgers),
    [preview]
  );


  // ── Allocation grid columns ───────────────────────────────────
  const allocationColumns = useMemo(() => [
    {
      title:     '#',
      width:     45,
      align:     'center',
      render:    (_, __, idx) => idx + 1
    },
    {
      title:     'Bill No',
      dataIndex: 'purchaseBillNo',
      width:     150,
      render:    (v) => v ? <Text code>{v}</Text> : '—'
    },
    {
      title:     'Supplier Invoice No',
      dataIndex: 'supplierInvoiceNo',
      width:     160,
      render:    (v) => v || '—'
    },
    {
      title:     'Invoice Date',
      dataIndex: 'supplierInvoiceDate',
      width:     110,
      render:    (v, row) => fmtDate(v || row.purchaseBillDate)
    },
    {
      title:     'Payable',
      dataIndex: 'payableAmount',
      width:     110,
      align:     'right',
      render:    (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoneyAlways(v)}</span>
    },
    {
      title:     'Already Paid',
      dataIndex: 'paidAmount',
      width:     110,
      align:     'right',
      render:    (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoneyAlways(v)}</span>
    },
    {
      title:     'Balance Before',
      dataIndex: 'billOutstandingBefore',
      width:     130,
      align:     'right',
      render:    (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoneyAlways(v)}</span>
    },
    {
      title:     'Allocation',
      dataIndex: 'allocationAmount',
      width:     140,
      align:     'right',
      render:    (v, row, idx) => (
        <InputNumber
          size="small"
          min={0}
          max={Math.max(Number(row.billOutstandingBefore) || 0, Number(v) || 0)}
          step={0.01}
          precision={2}
          value={v}
          disabled={!isEditable}
          style={{ width: '100%' }}
          onChange={(val) => updateAllocation(idx, { allocationAmount: Number(val) || 0 })}
        />
      )
    },
    {
      title:     'Balance After',
      dataIndex: 'billOutstandingAfter',
      width:     130,
      align:     'right',
      render:    (v) => {
        const n = Number(v) || 0;
        return (
          <span style={{
            fontVariantNumeric: 'tabular-nums',
            color: Math.abs(n) <= 0.01 ? '#16a34a' : (n < 0 ? '#dc2626' : undefined)
          }}>
            {fmtMoneyAlways(v)}
          </span>
        );
      }
    },
    {
      title:     'Remarks',
      dataIndex: 'remarks',
      ellipsis:  true,
      render:    (v, _row, idx) => (
        <Input
          size="small"
          value={v || ''}
          disabled={!isEditable}
          placeholder="—"
          onChange={(e) => updateAllocation(idx, { remarks: e.target.value })}
        />
      )
    },
    {
      title:  '',
      width:  44,
      align:  'center',
      fixed:  'right',
      render: (_, __, idx) => (
        <Tooltip title="Remove allocation">
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={!isEditable}
            onClick={() => removeAllocation(idx)}
          />
        </Tooltip>
      )
    }
  ], [isEditable, updateAllocation, removeAllocation]);


  // ── Ledger mapping columns (preview) ──────────────────────────
  const ledgerColumns = useMemo(() => [
    {
      title:     'Role',
      dataIndex: 'role',
      width:     220,
      render:    (v) => <span style={{ fontWeight: 500 }}>{v}</span>
    },
    {
      title:     'Account Code',
      dataIndex: 'accountCode',
      width:     200,
      render:    (v) => v
        ? <Text code>{v}</Text>
        : <Tag color="error" icon={<WarningOutlined />}>Missing</Tag>
    },
    {
      title:     'Account Name',
      dataIndex: 'accountName',
      render:    (v) => v
        ? <span>{v}</span>
        : <Text type="danger">No ledger bound for this role.</Text>
    }
  ], []);

  const ledgerRowClassName = useCallback((row) =>
    row && row.missing ? 'spay-ledger-row--missing' : '', []);


  // ── Add-Bill drawer columns ───────────────────────────────────
  const drawerColumns = useMemo(() => [
    {
      title:  '',
      width:  44,
      render: (_, row) => {
        const checked = drawerSelectedIds.includes(row.purchaseBillId);
        return (
          <Checkbox
            checked={checked}
            onChange={(e) => {
              const id = row.purchaseBillId;
              if (e.target.checked) {
                setDrawerSelectedIds((p) => p.concat(id));
              } else {
                setDrawerSelectedIds((p) => p.filter((x) => x !== id));
              }
            }}
          />
        );
      }
    },
    {
      title:     'Bill No',
      dataIndex: 'purchaseBillNo',
      render:    (v) => v ? <Text code>{v}</Text> : '—'
    },
    {
      title:     'Supplier Invoice',
      dataIndex: 'supplierInvoiceNo',
      render:    (v) => v || '—'
    },
    {
      title:     'Bill Date',
      dataIndex: 'purchaseBillDate',
      render:    (v) => fmtDate(v)
    },
    {
      title:     'Payable',
      dataIndex: 'payableAmount',
      align:     'right',
      render:    (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoneyAlways(v)}</span>
    },
    {
      title:     'Paid',
      dataIndex: 'paidAmount',
      align:     'right',
      render:    (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoneyAlways(v)}</span>
    },
    {
      title:     'Outstanding',
      dataIndex: 'outstandingAmount',
      align:     'right',
      render:    (v) => (
        <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
          {fmtMoneyAlways(v)}
        </Text>
      )
    },
    {
      title:     'Status',
      dataIndex: 'balanceStatus',
      width:     130,
      render:    (v) => v
        ? <Tag color={v === 'UNPAID' ? 'warning' : 'processing'}>{v}</Tag>
        : '—'
    }
  ], [drawerSelectedIds]);


  // ── Render ───────────────────────────────────────────────────
  const showBank   = showBankFor(paymentMethod);
  const showCheque = showChequeFor(paymentMethod);
  const showUtr    = showUtrFor(paymentMethod);
  const showAllocations = String(paymentType || '').toUpperCase() === 'BILL_WISE';

  return (
    <div className="p-4">
      {/* Page head */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <Title level={4} style={{ marginBottom: 0 }}>
            <DollarOutlined style={{ marginInlineEnd: 8 }} />
            {isCreate ? 'New Supplier Payment' : 'Supplier Payment'}
          </Title>
          <Text type="secondary">
            Capture the payment, allocate it against unpaid bills (if
            BILL_WISE), then preview and post to accounts. Backend
            recomputes on post and is the final authority.
          </Text>
        </div>
        <Space>
          {!isCreate ? (
            <Button
              icon={<ReloadOutlined />}
              onClick={loadPayment}
              disabled={loading || saving || posting || cancelling}
            >
              Refresh
            </Button>
          ) : null}
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Space>
      </div>

      {/* Load error */}
      {loadError ? (
        <Alert
          type="error"
          showIcon
          message="Could not load payment"
          description={loadError}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={loadPayment}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          disabled={!isEditable}
          requiredMark="optional"
        >
          {/* Header card — payment identity + instrument fields */}
          <Card
            size="small"
            style={{ marginBottom: 12 }}
            title={
              <Space wrap>
                <DollarOutlined />
                <span>Payment Details</span>
                {payment && payment.paymentNo ? (
                  <Tag color="blue">{payment.paymentNo}</Tag>
                ) : null}
                {status ? (
                  <Tag color={paymentStatusColor(status)}>
                    {paymentStatusLabel(status)}
                  </Tag>
                ) : null}
                {postingStatus ? (
                  <Tag color={postingStatusColor(postingStatus)}>
                    Posting: {postingStatusLabel(postingStatus)}
                  </Tag>
                ) : null}
                {voucherNo ? (
                  <Tag color="success">Voucher: {voucherNo}</Tag>
                ) : null}
              </Space>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
              {/* Payment No — readonly badge (generated server-side) */}
              <Form.Item label="Payment No">
                <Input
                  value={(payment && payment.paymentNo) || '(generated on save)'}
                  readOnly
                  disabled
                />
              </Form.Item>

              <Form.Item
                label="Payment Date"
                name="paymentDate"
                rules={[{ required: true, message: 'Payment date is required.' }]}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>

              <Form.Item
                label="Supplier"
                name="supplierId"
                rules={[{ required: true, message: 'Supplier is required.' }]}
              >
                <Select
                  placeholder="Select supplier…"
                  showSearch
                  filterOption={false}
                  onSearch={loadSuppliers}
                  {...buildDropdownStateProps({
                    loading: supplierLoading,
                    error:   supplierError,
                    data:    supplierOptions
                  })}
                  options={supplierOptions.map((s) => ({
                    value: s.id || s.supplierId,
                    label: (
                      <span>
                        {s.supplierCode ? (
                          <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>
                            {s.supplierCode}
                          </code>
                        ) : null}
                        {s.supplierName || s.name || '(unnamed)'}
                      </span>
                    )
                  }))}
                />
              </Form.Item>

              <Form.Item label="Supplier GSTIN" name="supplierGstin">
                <Input placeholder="Auto-filled from supplier" />
              </Form.Item>

              <Form.Item
                label="Payment Type"
                name="paymentType"
                rules={[{ required: true, message: 'Payment type is required.' }]}
              >
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  options={PAYMENT_TYPES.map((t) => ({
                    label: t.replace(/_/g, ' '),
                    value: t
                  }))}
                />
              </Form.Item>

              <Form.Item
                label="Payment Method"
                name="paymentMethod"
                rules={[{ required: true, message: 'Payment method is required.' }]}
              >
                <Select
                  options={PAYMENT_METHODS.map((m) => ({
                    value: m,
                    label: paymentMethodLabel(m)
                  }))}
                />
              </Form.Item>

              <Form.Item
                label="Payment Account"
                name="paymentAccountId"
                rules={[{
                  required: true,
                  message:  'Payment account is required.'
                }]}
              >
                <Select
                  placeholder="Select payment ledger…"
                  showSearch
                  filterOption={false}
                  onSearch={loadLedgers}
                  {...buildDropdownStateProps({
                    loading: ledgerLoading,
                    error:   ledgerError,
                    data:    ledgerOptions
                  })}
                  options={ledgerOptions.map((l) => ({
                    value: l.id || l.accountId,
                    label: (
                      <span>
                        {(l.accountCode || l.account_code) ? (
                          <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>
                            {l.accountCode || l.account_code}
                          </code>
                        ) : null}
                        {l.accountName || l.account_name || '(unnamed)'}
                      </span>
                    )
                  }))}
                />
              </Form.Item>

              <Form.Item label="Reference No" name="referenceNo">
                <Input placeholder="Internal reference / voucher ref" />
              </Form.Item>

              <Form.Item label="Reference Date" name="referenceDate">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>

              {showBank ? (
                <Form.Item label="Bank Name" name="bankName">
                  <Input placeholder="Counterparty bank name" />
                </Form.Item>
              ) : null}

              {showCheque ? (
                <Form.Item label="Cheque No" name="chequeNo">
                  <Input placeholder="Cheque number" />
                </Form.Item>
              ) : null}

              {showUtr ? (
                <Form.Item label="UTR No" name="utrNo">
                  <Input placeholder="UTR / transaction reference" />
                </Form.Item>
              ) : null}

              <Form.Item
                label="Payment Amount"
                name="paymentAmount"
                rules={[
                  { required: true, message: 'Payment amount is required.' },
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') return Promise.resolve();
                      const n = Number(value);
                      if (!(n > 0)) return Promise.reject(new Error('Must be greater than zero.'));
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  prefix="₹"
                />
              </Form.Item>

              <Form.Item label="Round Off" name="roundOff">
                <InputNumber
                  style={{ width: '100%' }}
                  step={0.01}
                  precision={2}
                  prefix="₹"
                />
              </Form.Item>
            </div>

            <Form.Item label="Remarks" name="remarks" style={{ marginBottom: 0 }}>
              <Input.TextArea rows={2} placeholder="Optional notes for this payment" />
            </Form.Item>
          </Card>

          {/* Frontend validation summary — surfaces blockers before
              the operator hits Save / Post. */}
          {validationErrors.length > 0 && isEditable ? (
            <Alert
              type="warning"
              showIcon
              icon={<ExclamationCircleOutlined />}
              message={`${validationErrors.length} validation issue${validationErrors.length === 1 ? '' : 's'}`}
              description={
                <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                  {validationErrors.map((e, idx) => (
                    <li key={idx}>{e}</li>
                  ))}
                </ul>
              }
              style={{ marginBottom: 12 }}
            />
          ) : null}

          {/* Ledger Mapping panel (preview-driven) */}
          {preview ? (
            <Card
              size="small"
              title={
                <Space>
                  <BankOutlined />
                  <span>Ledger Mapping</span>
                  <Text type="secondary">
                    ({ledgerRows.length} role{ledgerRows.length === 1 ? '' : 's'})
                  </Text>
                </Space>
              }
              style={{ marginBottom: 12 }}
            >
              {ledgerRows.length > 0 ? (
                <Table
                  size="small"
                  rowKey="key"
                  columns={ledgerColumns}
                  dataSource={ledgerRows}
                  pagination={false}
                  rowClassName={ledgerRowClassName}
                  locale={{ emptyText: ' ' }}
                />
              ) : (
                <Empty description="No ledger mapping available." />
              )}

              {/* Preview blockers — surfaces backend reasons canPost
                  may be false (missing SUPPLIER-ADVANCE, locked period,
                  duplicate voucher, etc.). */}
              {Array.isArray(preview.blockers) && preview.blockers.length > 0 ? (
                <Alert
                  type={preview.canPost ? 'warning' : 'error'}
                  showIcon
                  message={
                    preview.canPost
                      ? `${preview.blockers.length} warning${preview.blockers.length === 1 ? '' : 's'} on this preview`
                      : `Posting blocked — ${preview.blockers.length} issue${preview.blockers.length === 1 ? '' : 's'} must be resolved`
                  }
                  description={
                    <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                      {preview.blockers.map((b, idx) => (
                        <li key={idx}>
                          {b && b.code ? (
                            <Text code style={{ marginInlineEnd: 6 }}>{b.code}</Text>
                          ) : null}
                          <span>{(b && b.message) || 'Unknown issue.'}</span>
                        </li>
                      ))}
                    </ul>
                  }
                  style={{ marginTop: 12 }}
                />
              ) : null}
            </Card>
          ) : null}

          {/* Allocation Grid — BILL_WISE only */}
          {showAllocations ? (
            <Card
              size="small"
              title={
                <Space>
                  <span>Bill Allocations</span>
                  <Text type="secondary">
                    ({allocations.length} line{allocations.length === 1 ? '' : 's'})
                  </Text>
                </Space>
              }
              extra={
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  type="primary"
                  onClick={openAddBillDrawer}
                  disabled={!isEditable || !supplierId}
                >
                  Add Bill
                </Button>
              }
              style={{ marginBottom: 12 }}
            >
              <Table
                size="small"
                rowKey="key"
                columns={allocationColumns}
                dataSource={allocations}
                pagination={false}
                scroll={{ x: 1280 }}
                locale={{
                  emptyText: supplierId
                    ? 'No allocations yet — click "Add Bill" to select unpaid bills.'
                    : 'Select a supplier first.'
                }}
              />
            </Card>
          ) : null}

          {/* Summary card */}
          <Card size="small" style={{ marginBottom: 12 }}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Statistic
                title="Payment Amount"
                value={fmtMoneyAlways(paymentAmount || 0)}
                prefix="₹"
                valueStyle={{ fontVariantNumeric: 'tabular-nums' }}
              />
              <Statistic
                title="Allocated"
                value={fmtMoneyAlways(allocationTotals.allocated)}
                prefix="₹"
                valueStyle={{ fontVariantNumeric: 'tabular-nums' }}
              />
              <Statistic
                title="Unallocated"
                value={fmtMoneyAlways(allocationTotals.unallocated)}
                prefix="₹"
                valueStyle={{ fontVariantNumeric: 'tabular-nums' }}
              />
              <Statistic
                title="Difference"
                value={fmtMoneyAlways(allocationTotals.difference)}
                prefix="₹"
                valueStyle={{
                  color: Math.abs(allocationTotals.difference) <= 0.01
                    ? '#16a34a'
                    : '#dc2626',
                  fontVariantNumeric: 'tabular-nums'
                }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3 pt-3 border-t border-slate-100">
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Voucher No</Text>
                <div style={{ fontWeight: 500 }}>
                  {voucherNo
                    ? <Tag color="success">{voucherNo}</Tag>
                    : <Text type="secondary">—</Text>}
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Posting Status</Text>
                <div style={{ fontWeight: 500 }}>
                  {postingStatus
                    ? (
                      <Tag color={postingStatusColor(postingStatus)}>
                        {postingStatusLabel(postingStatus)}
                      </Tag>
                    )
                    : <Text type="secondary">—</Text>}
                </div>
              </div>
              <div className="flex items-center">
                {Math.abs(allocationTotals.difference) <= 0.01 ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    Allocations balanced
                  </Tag>
                ) : (
                  <Tag color="error" icon={<CloseCircleOutlined />}>
                    {showAllocations ? 'Allocations unbalanced' : 'No allocations'}
                  </Tag>
                )}
              </div>
            </div>
          </Card>
        </Form>

        {/* Post result (replaces toolbar after a post attempt) */}
        {postResult ? (
          <Card size="small" style={{ marginBottom: 12 }}>
            <Result
              status={postResult.success ? 'success' : 'error'}
              title={
                postResult.success
                  ? `Posted as ${postResult.voucherNo || '(accounting voucher)'}`
                  : 'Posting did not complete'
              }
              subTitle={
                <Space direction="vertical" size={4}>
                  <span>
                    Posting No:&nbsp;<strong>{postResult.postingNo || '—'}</strong>
                    {' · '}
                    Voucher No:&nbsp;<strong>{postResult.voucherNo || '—'}</strong>
                  </span>
                  <span>
                    Status:&nbsp;
                    <Tag color={postingStatusColor(postResult.status)}>
                      {postingStatusLabel(postResult.status)}
                    </Tag>
                  </span>
                  {postResult.postedAt ? (
                    <span>
                      Posted At:&nbsp;<strong>{fmtDateTime(postResult.postedAt)}</strong>
                    </span>
                  ) : null}
                  {postResult.errorMessage ? (
                    <Paragraph type="danger" style={{ marginBottom: 0 }}>
                      {postResult.errorMessage}
                    </Paragraph>
                  ) : null}
                </Space>
              }
              extra={[
                <Button
                  key="back"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)}
                >
                  Back
                </Button>,
                <Button
                  key="refresh"
                  type="primary"
                  onClick={() => {
                    setPostResult(null);
                    setPostError(null);
                    loadPayment();
                  }}
                >
                  Refresh
                </Button>
              ]}
            />
          </Card>
        ) : null}

        {/* Post error (kept above the toolbar so the operator sees it) */}
        {postError && !postResult ? (
          <Alert
            type="error"
            showIcon
            message="Posting failed"
            description={postError}
            style={{ marginBottom: 12 }}
            closable
            onClose={() => setPostError(null)}
          />
        ) : null}

        {/* Footer toolbar */}
        {!postResult ? (
          <div className="flex items-center justify-end gap-2">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              disabled={saving || posting || cancelling}
            >
              Back
            </Button>
            {canCancel ? (
              <Button
                danger
                icon={<StopOutlined />}
                loading={cancelling}
                disabled={saving || posting}
                onClick={handleCancel}
              >
                Cancel Payment
              </Button>
            ) : null}
            {isEditable ? (
              <Button
                icon={<SaveOutlined />}
                loading={saving}
                disabled={posting || cancelling}
                onClick={handleSaveDraft}
              >
                Save Draft
              </Button>
            ) : null}
            {!isCreate && payment && payment.id ? (
              <Tooltip
                title={
                  !payment || !payment.id
                    ? 'Save the draft first.'
                    : ''
                }
              >
                <Button
                  icon={<EyeOutlined />}
                  loading={previewing}
                  disabled={saving || posting || cancelling || !payment || !payment.id}
                  onClick={handlePreviewPosting}
                >
                  Preview Posting
                </Button>
              </Tooltip>
            ) : null}
            {isEditable ? (
              <Tooltip
                title={
                  !payment || !payment.id
                    ? 'Save the draft first.'
                    : !canPost
                      ? (validationErrors.length > 0
                          ? 'Resolve the validation issues above.'
                          : 'This payment is not ready for posting.')
                      : ''
                }
              >
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={posting}
                  disabled={!canPost || saving || cancelling || !payment || !payment.id}
                  onClick={handlePost}
                >
                  Post Payment
                </Button>
              </Tooltip>
            ) : null}
          </div>
        ) : null}
      </Spin>

      {/* Add-Bill drawer — UNPAID / PARTIALLY_PAID bills for the
          selected supplier. Multi-select; on confirm the rows are
          merged into the allocation grid with the outstanding balance
          pre-filled as the default allocation amount. */}
      <Drawer
        title={
          <Space>
            <PlusOutlined />
            <span>Add Bills for Allocation</span>
            <Text type="secondary">
              {drawerRows.length} unpaid bill{drawerRows.length === 1 ? '' : 's'}
            </Text>
          </Space>
        }
        width={920}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={drawerSelectedIds.length === 0}
              onClick={handleAddBillsConfirm}
            >
              Add {drawerSelectedIds.length || ''} Bill{drawerSelectedIds.length === 1 ? '' : 's'}
            </Button>
          </div>
        }
      >
        <Spin spinning={drawerLoading}>
          <Table
            size="small"
            rowKey={(row) => row.purchaseBillId || `${row.purchaseBillNo}-${row.supplierInvoiceNo}`}
            columns={drawerColumns}
            dataSource={drawerRows}
            pagination={{ pageSize: 25, showSizeChanger: false }}
            scroll={{ x: 820 }}
            locale={{
              emptyText: drawerLoading
                ? ' '
                : 'No unpaid bills found for this supplier.'
            }}
          />
        </Spin>
      </Drawer>

      {/* Local styles — match the Stage 6P.6 preview page palette so
          the supplier-payment page feels like part of the same set. */}
      <style>{`
        .spay-ledger-row--missing > td { background-color: #fef2f2 !important; }
        .spay-ledger-row--missing:hover > td { background-color: #fee2e2 !important; }
      `}</style>
    </div>
  );
}
